import type { RichTextField } from "@prismicio/client"

import type { Nullable } from "@bounce/util"

/**
 * Extract the first heading and all its content until the next heading
 * @param content - The rich text content to filter
 * @returns The filtered content or null if empty
 */
export const extractFirstSection = (content: Nullable<RichTextField>): Nullable<RichTextField> => {
  if (!content) return null

  const filteredArray = content.reduce(
    (acc: { filtered: any[]; foundFirstHeading: boolean; endOfSection: boolean }, item: any) => {
      if (acc.endOfSection) {
        return acc
      }

      if (acc.foundFirstHeading) {
        if (item.type?.startsWith("heading")) {
          acc.endOfSection = true
          return acc
        }
        acc.filtered.push(item)
        return acc
      }

      if (item.type?.startsWith("heading")) {
        acc.foundFirstHeading = true
        acc.filtered.push(item)
      }

      return acc
    },
    { filtered: [], foundFirstHeading: false, endOfSection: false },
  ).filtered

  return filteredArray.length > 0 ? (filteredArray as RichTextField) : null
}

/**
 * Filter content to remove sections where headings mention specific words
 * @param content - The rich text content to filter
 * @param wordsToFilter - Words to check for in headings
 * @returns The filtered content or null if empty
 */
export const removeContentWithWords = (
  content: Nullable<RichTextField>,
  wordsToFilter: string[],
): Nullable<RichTextField> => {
  if (!content) return null

  const filteredArray = content.reduce(
    (acc: { filtered: any[]; skipUntilIndex: number }, item: any, index: number) => {
      const isHeadingWithFilteredWord =
        item.type?.startsWith("heading") && "text" in item && wordsToFilter.some(word => item.text.includes(word))

      if (isHeadingWithFilteredWord) {
        const nextHeadingIndex = content.findIndex(
          (nextItem, nextIndex) => nextIndex > index && nextItem.type.startsWith("heading"),
        )

        acc.skipUntilIndex = nextHeadingIndex !== -1 ? nextHeadingIndex : content.length
        return acc
      }

      if (index < acc.skipUntilIndex) {
        return acc
      }

      acc.filtered.push(item)
      return acc
    },
    { filtered: [], skipUntilIndex: -1 },
  ).filtered

  return filteredArray.length > 0 ? (filteredArray as RichTextField) : null
}
